import { FC, memo, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { CampaignTypesDto, ExtendedCampaignRead, useGetCampaignQuery } from '@api/apiCompat';
import { BASIC_INFO } from '@constants/formFields';
import { useLeadScraperContext } from '@pages/LeadScraper/context';
import { useCampaignTypes } from '@hooks/api/useCampaignTypes';

import { Skeleton } from '@mui/lab';
import { Box, Button, TextField, Typography, Chip } from '@mui/material';

import { transformData } from '../../utils/dataToOptions';

const BasicInformationForm: FC<{ campaignTypes?: CampaignTypesDto[] }> = ({ campaignTypes }) => {
  const [, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const { control, formState, setValue, trigger } = useFormContext();
  const { isEdit, campaignTypeForm } = useLeadScraperContext();
  const { data: apiCampaignTypes } = useCampaignTypes();
  const typeOptions = transformData(campaignTypes);

  const { campaignId } = useParams();
  const { data: rawCampaignData } = useGetCampaignQuery(
    { id: campaignId as string },
    { refetchOnMountOrArgChange: true, skip: !campaignId },
  );
  // Cast to extended type for additional properties
  const campaignData = rawCampaignData as ExtendedCampaignRead | undefined;

  // Get selected campaign type from Step 1
  const { campaignType: selectedCampaignType } = campaignTypeForm.watch();
  const selectedTypeName = apiCampaignTypes?.find(
    t => String(t.campaign_type_id) === selectedCampaignType
  )?.type_name;

  // Get campaign_type_id from extended type or parse from campaign_type
  const campaignTypeId = campaignData?.campaign_type_id ??
    (typeof campaignData?.campaign_type === 'number' ? campaignData.campaign_type :
     typeof campaignData?.campaign_type === 'object' ? (campaignData.campaign_type as any)?.id : undefined);

  useEffect(() => {
    if (isEdit && campaignData && campaignTypeId) {
      setSearchParams({ campaignId: String(campaignTypeId) });
      setValue(BASIC_INFO.TYPE, typeOptions.find(opt => opt.campaignId && +opt.campaignId === campaignTypeId)?.campaignId);
      setValue(BASIC_INFO.CAMPAIGN_NAME, campaignData?.name);
      setValue(BASIC_INFO.CAMPAIGN_DESCRIPTION, campaignData?.description || '');
      trigger();
    }
  }, [campaignData, setValue]);

  // Sync the type from campaignTypeForm to basicInfoForm when not editing
  useEffect(() => {
    if (!isEdit && selectedCampaignType) {
      setValue(BASIC_INFO.TYPE, selectedCampaignType);
    }
  }, [selectedCampaignType, isEdit, setValue]);

  return (
    <Box display="flex" flexDirection="column" width="100%" pt={2} pb={1} gap={2}>
      {/* Show selected category as a read-only chip instead of dropdown */}
      {selectedTypeName && !isEdit && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Selected Category
          </Typography>
          <Chip
            label={selectedTypeName}
            color="primary"
            variant="outlined"
            sx={{ fontSize: '0.875rem', height: 36 }}
          />
        </Box>
      )}

      <Controller
        control={control}
        name={BASIC_INFO.CAMPAIGN_NAME}
        render={({ field, fieldState }) => (
          <TextField
            fullWidth
            variant="outlined"
            label={t('dashboard.newScraper.settings.scraperNameLabel')}
            type="text"
            error={Boolean(fieldState.error)}
            helperText={
              fieldState.error &&
              t(`common.errors.${fieldState.error.type}`, { defaultValue: fieldState.error.message })
            }
            {...field}
          />
        )}
      />

      <Controller
        control={control}
        name={BASIC_INFO.CAMPAIGN_DESCRIPTION}
        render={({ field, fieldState }) => (
          <TextField
            fullWidth
            variant="outlined"
            label={t('dashboard.newScraper.settings.scraperDescription')}
            type="text"
            error={Boolean(fieldState.error)}
            helperText={
              fieldState.error &&
              t(`common.errors.${fieldState.error.type}`, { defaultValue: fieldState.error.message })
            }
            {...field}
          />
        )}
      />

      <Button fullWidth variant="contained" color="primary" type="submit" disabled={!formState.isValid}>
        {t('dashboard.newScraper.nextStep')}
      </Button>
    </Box>
  );
};

export default memo(BasicInformationForm);
